import React, { useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Radio from '@mui/material/Radio';
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import { isMobile } from "react-device-detect";
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';

import { displayError } from "../../../helpers";
import { InfoIcon } from "../../../components/Icons";
import { donationActions } from "../../../store/actions";
import { useNonInitialEffect } from "../../../hooks/UseNonInitialEffect";
import { LightTooltip } from "../../../components/StyledComponents/LightToolTip";
import { GoalAmountInput, SwitchWithLabel, Textfield } from "../../../components";
import { DONATION_IMPACT_TYPES, SINGLE_OWNER_PAGE, TEAM_PAGE } from "../../../constants";

export default function ({ state, handleChange, editView, validationErrors }) {

    const dispatch = useDispatch();
    const [openTooltip, setOpenTooltip] = useState(false);
    const productImpact = useSelector(state => state.donation.product_impact);
    const locationDetails = useSelector(state => state.fundraiser_page.location_details);

    const impactMessage = useMemo(
        () => {
            if (state.fundraiseForFlood)
                return "Your donation supports the flood relief affectees in Pakistan"

            else if (productImpact?.product_impact > 1)
                return `Your fundraiser will support the education of ${productImpact.product_impact} children for a year`

            else return "Your fundraiser will support TCF’s general education fund"
        },
        [productImpact, state.fundraiseForFlood]
    );

    useNonInitialEffect(() => {
        if (!state.fundraiseForFlood) {
            const calculateImpact = setTimeout(() => {
                dispatch(donationActions.calculateProductImpact(state.selectedCurrency.id, state.goal, DONATION_IMPACT_TYPES.annual))
            }, 500)

            return () => clearTimeout(calculateImpact)
        }
    }, [state.goal, state.selectedCurrency])

    useNonInitialEffect(() => {
        if (!editView && locationDetails?.is_exist && state.selectedCurrency.id !== locationDetails.currency_id) {
            handleChange(locationDetails, true)
        }
    }, [locationDetails])

    return <Stack gap={3} className="BasicInfoTab">
        <Textfield
            name="name"
            label="Give your page a name"
            value={state.name}
            onChange={handleChange}
            error={displayError(validationErrors?.name)}
            helperText={validationErrors.name}
            required
            autoFocus
            fullWidth
        />
        <Stack direction={{ xs: 'column', md: 'row' }}>
            <GoalAmountInput
                selectedCurrencyOption={state.selectedCurrency.name}
                currencyChangeHandler={(currency) => handleChange(currency, true)}
                disableCurrencyEdit={editView}
                amountProps={{
                    id: "goal",
                    name: "goal",
                    placeholder: "Enter your fundraising goal",
                    value: state.goal,
                    onChange: handleChange,
                    // onKeyPress: validateDonationAmount,
                    maxLength: "15",
                    error: displayError(validationErrors.goal),
                    helpertext: validationErrors.goal
                }}
            />
            <Box className="GoalImpactMessageBox">
                <Typography>{impactMessage}</Typography>
            </Box>
        </Stack>

        <FormControl>
            <FormLabel>
                <Typography className="FundraiserTypeLabel">I am fundraising</Typography>
            </FormLabel>
            <RadioGroup name="pageType" value={state.pageType} onChange={e => handleChange(e)}>
                <Stack className="FundraiserTypeStack" direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1, md: 8 }} divider={<Divider orientation="vertical" flexItem />}>
                    <Box>
                        <FormControlLabel className="TypeRadioLabel" value={SINGLE_OWNER_PAGE} control={<Radio size="small" />} label="as an individual" />
                        <Box className="IndDesc">
                            <Typography className="FirstChild" gutterBottom>I will raise funds on my own</Typography>
                            <Typography className="SecondChild" >Note: </Typography>
                            <Typography variant="small">Team fundraising can be enabled later.</Typography>
                        </Box>
                    </Box>
                    <Box>
                        <FormControlLabel className="TypeRadioLabel" value={TEAM_PAGE} control={<Radio size="small" />} label="in a team" />
                        <Typography className="TeamDesc">I will invite friends to raise funds with me</Typography>
                    </Box>
                </Stack>
            </RadioGroup>
        </FormControl>

        <Stack className="KeepPagePrivateStack">
            <SwitchWithLabel nameAndId="keepPagePrivate" className="Switch" label="Keep my Page private" checked={state.keepPagePrivate} onChange={e => handleChange(e)} />
            <LightTooltip popperSx={{marginTop: isMobile ? "0" : undefined}} open={isMobile ? openTooltip : undefined} onClick={() => setOpenTooltip(!openTooltip)} title="Private fundraisers will not appear in search results but will be accessible through direct links." placement={isMobile ? "bottom" : "right"}>
                <IconButton>
                    <InfoIcon />
                </IconButton>
            </LightTooltip>
        </Stack>
    </Stack>
}
